import React, { useState, useRef, useCallback, useEffect } from "react";
import { API_URL } from "../config";
import "./VideoSet.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

/* --- Slot card --- */
function SlotCard({ slot, index, onRemove, onClickEmpty, onAliasChange }) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);

  if (!slot) {
    return (
      <div className="vs-slot vs-slot--empty" onClick={() => onClickEmpty(index)}>
        <span className="vs-slot__num">{index + 1}</span>
        <span className="vs-slot__plus">+</span>
      </div>
    );
  }

  const typeClass = `vs-slot--${slot.type}`;
  const handleBlur = () => {
    setEditing(false);
    const val = inputRef.current?.value.trim().split(/\s/)[0] || slot.alias;
    if (val) onAliasChange(index, val);
  };

  return (
    <div className={`vs-slot vs-slot--filled ${typeClass}`}>
      <img src={slot.url} alt={slot.name} className="vs-slot__img" />
      <div className="vs-slot__info">
        {editing ? (
          <input
            ref={inputRef}
            className="vs-slot__alias-input"
            defaultValue={slot.alias}
            autoFocus
            onBlur={handleBlur}
            onKeyDown={e => { if (e.key === "Enter") handleBlur(); }}
            maxLength={20}
          />
        ) : (
          <span className="vs-slot__alias" onClick={() => setEditing(true)}>@{slot.alias}</span>
        )}
        <span className="vs-slot__name">{slot.name}</span>
      </div>
      <button className="vs-slot__remove" onClick={() => onRemove(index)}>&times;</button>
    </div>
  );
}

/* --- Asset picker modal --- */
function AssetPicker({ scenes, characters, artifacts, onSelect, onClose }) {
  const [tab, setTab] = useState("scenes");

  const items = tab === "scenes" ? scenes
    : tab === "characters" ? characters.flatMap(c =>
        c.sheets.map(s => ({
          name: s.name.replace(".png", "").replace(/_/g, " "),
          url: s.url,
          type: "character",
          charName: c.displayName,
          alias: c.displayName.split(/\s/)[0],
        }))
      )
    : artifacts.map(a => ({
        ...a,
        type: "artifact",
        alias: a.name.replace(".png", "").replace(/_/g, " ").split(/\s/)[0],
      }));

  return (
    <div className="vs-picker-overlay" onClick={onClose}>
      <div className="vs-picker" onClick={e => e.stopPropagation()}>
        <div className="vs-picker__header">
          <h3>Añadir al set</h3>
          <button className="vs-picker__close" onClick={onClose}>&times;</button>
        </div>
        <div className="vs-picker__tabs">
          <button className={tab === "scenes" ? "active" : ""} onClick={() => setTab("scenes")}>Escenas</button>
          <button className={tab === "characters" ? "active" : ""} onClick={() => setTab("characters")}>Personajes</button>
          <button className={tab === "artifacts" ? "active" : ""} onClick={() => setTab("artifacts")}>Artefactos</button>
        </div>
        <div className="vs-picker__grid">
          {items.length === 0 ? (
            <div className="vs-picker__empty">Sin elementos</div>
          ) : items.map((item, i) => {
            const displayName = item.charName
              ? `${item.charName} — ${item.name}`
              : item.name.replace(".png", "").replace(/_/g, " ");
            return (
              <div key={i} className="vs-picker__item" onClick={() => {
                const type = tab === "scenes" ? "scene" : item.type || "artifact";
                const alias = type === "scene"
                  ? displayName.split(/\s/)[0]
                  : item.alias || displayName.split(/\s/)[0];
                onSelect({ name: displayName, url: item.url, type, alias });
              }}>
                <img src={item.url} alt={displayName} />
                <span>{displayName}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* --- Persistence --- */
const STORAGE_KEY = "720mt_videoset";

function loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveSession(data) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
}

/* --- Main page --- */
export default function VideoSet() {
  const headers = { Authorization: `Bearer ${getToken()}` };

  // Data sources
  const [characters, setCharacters] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [artifacts, setArtifacts] = useState([]);

  // Restore session
  const saved = useRef(loadSession());

  // 9 slots for references
  const [slots, setSlots] = useState(saved.current?.slots || Array(9).fill(null));
  const [pickerIndex, setPickerIndex] = useState(null);
  const [setAccepted, setSetAccepted] = useState(saved.current?.setAccepted || false);

  // Reel ref for accepted set strip
  const reelRef = useRef(null);
  const scrollReel = (dir) => {
    if (reelRef.current) reelRef.current.scrollBy({ left: dir * 150, behavior: "smooth" });
  };

  // Prompt
  const [brief, setBrief] = useState(saved.current?.brief || "");
  const [prompt, setPrompt] = useState(saved.current?.prompt || "");
  const [generating, setGenerating] = useState(false);
  const promptRef = useRef(null);

  // Refine chat
  const [refineInput, setRefineInput] = useState("");
  const [refining, setRefining] = useState(false);
  const [chatHistory, setChatHistory] = useState(saved.current?.chatHistory || []);

  // Video params
  const [ratio, setRatio] = useState(saved.current?.ratio || "16:9");
  const [duration, setDuration] = useState(saved.current?.duration || "5");
  const [resolution, setResolution] = useState(saved.current?.resolution || "480p");
  const [soundFx, setSoundFx] = useState(saved.current?.soundFx ?? true);

  // Video generation
  const [videoStatus, setVideoStatus] = useState("idle");
  const [videoUrl, setVideoUrl] = useState(saved.current?.videoUrl || null);

  // Auto-save session on state change
  useEffect(() => {
    saveSession({ slots, setAccepted, brief, prompt, chatHistory, ratio, duration, resolution, soundFx, videoUrl });
  }, [slots, setAccepted, brief, prompt, chatHistory, ratio, duration, resolution, soundFx, videoUrl]);
  const [errorMsg, setErrorMsg] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const videoPollingRef = useRef(false);
  const timerRef = useRef(null);

  const filterPlaceholders = (items) =>
    (items || []).filter((i) => !i.name?.includes(".emptyFolderPlaceholder"));

  // --- Load data ---
  const loadData = useCallback(async () => {
    try {
      const chRes = await fetch(`${API_URL}/characters`, { headers });
      const chData = await chRes.json();
      const chars = chData.characters || [];
      const detailed = await Promise.all(
        chars.map(async (c) => {
          const cid = c.id || c;
          try {
            const res = await fetch(`${API_URL}/characters/${cid}`, { headers });
            const detail = await res.json();
            const sheets = filterPlaceholders(detail?.sheets || []);
            return { id: cid, displayName: detail.display_name || c.display_name || cid, sheets };
          } catch {
            return { id: cid, displayName: c.display_name || cid, sheets: [] };
          }
        })
      );
      setCharacters(detailed);

      const scRes = await fetch(`${API_URL}/scenes`, { headers });
      const scData = await scRes.json();
      setScenes(filterPlaceholders(scData.backgrounds || []));

      const artRes = await fetch(`${API_URL}/artifacts`, { headers });
      const artData = await artRes.json();
      setArtifacts(filterPlaceholders(artData.artifacts || []));
    } catch {}
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // --- Slot management ---
  const filledSlots = slots.filter(Boolean);
  const slotCount = filledSlots.length;

  const assignSlot = (index, asset) => {
    setSlots(prev => {
      const next = [...prev];
      next[index] = asset;
      return next;
    });
    setPickerIndex(null);
  };

  const removeSlot = (index) => {
    setSlots(prev => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  };

  const updateAlias = (index, alias) => {
    setSlots(prev => {
      const next = [...prev];
      if (next[index]) next[index] = { ...next[index], alias };
      return next;
    });
  };

  // Build the prompt for the API: replace @alias with @Image1, @Image2...
  const buildApiPrompt = () => {
    let apiPrompt = prompt;
    const filled = slots.map((s, i) => s ? { ...s, originalIndex: i } : null).filter(Boolean);
    // Sort by alias length descending to avoid partial replacements
    const sorted = [...filled].sort((a, b) => b.alias.length - a.alias.length);
    // First pass: replace aliases with unique placeholders
    sorted.forEach((s) => {
      const imageIndex = filled.indexOf(s) + 1;
      const regex = new RegExp(`@${s.alias}\\b`, "gi");
      apiPrompt = apiPrompt.replace(regex, `__REF_${imageIndex}__`);
    });
    // Second pass: replace placeholders with @Image1, @Image2...
    filled.forEach((_, i) => {
      apiPrompt = apiPrompt.replaceAll(`__REF_${i + 1}__`, `@Image${i + 1}`);
    });
    return apiPrompt;
  };

  // Build image_urls array (filled slots in order)
  const buildImageUrls = () => slots.filter(Boolean).map(s => s.url);

  // --- Generate prompt via Gemini ---
  const handleGeneratePrompt = async () => {
    setGenerating(true);
    setErrorMsg("");
    try {
      const res = await fetch(`${API_URL}/video/generate-ref-prompt`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          brief: brief || "",
          slots: slots.map((s, i) => s ? { alias: s.alias, name: s.name, type: s.type } : null).filter(Boolean),
          duration,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      setPrompt(data.prompt || "");
    } catch (err) {
      setErrorMsg(err.message);
    }
    setGenerating(false);
  };

  // --- Refine prompt ---
  const handleRefine = async () => {
    if (!refineInput.trim()) return;
    setRefining(true);
    const userMsg = refineInput.trim();
    setRefineInput("");
    setChatHistory(prev => [...prev, { role: "user", text: userMsg }]);
    try {
      const charNames = slots.filter(s => s?.type === "character").map(s => s.alias);
      const res = await fetch(`${API_URL}/video/refine-prompt`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          current_prompt: prompt,
          instruction: userMsg,
          characters: charNames,
        }),
      });
      if (!res.ok) throw new Error("Error al refinar");
      const data = await res.json();
      setPrompt(data.prompt || prompt);
      setChatHistory(prev => [...prev, { role: "assistant", text: data.explanation || "Prompt actualizado." }]);
    } catch (err) {
      setChatHistory(prev => [...prev, { role: "assistant", text: `Error: ${err.message}` }]);
    }
    setRefining(false);
  };

  // --- Generate video ---
  const handleGenerateVideo = async () => {
    if (!prompt || slotCount === 0) return;
    setVideoStatus("generating");
    setVideoUrl(null);
    setErrorMsg("");
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed(prev => prev + 1), 1000);
    try {
      // Step 1: Backend submits to fal.ai and returns queue URLs
      const res = await fetch(`${API_URL}/video/generate-ref`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          prompt: buildApiPrompt(),
          image_urls: buildImageUrls(),
          ratio,
          duration,
          resolution,
          generate_audio: soundFx,
          character_names: slots.filter(s => s?.type === "character").map(s => s.name),
          scene_name: slots.find(s => s?.type === "scene")?.name || "",
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      // Step 2: Frontend polls fal.ai directly (bypass Vercel)
      pollFalDirectly(data);
    } catch (err) {
      setErrorMsg(err.message);
      setVideoStatus("error");
      clearInterval(timerRef.current);
    }
  };

  // Poll fal.ai directly from frontend — no Vercel timeout issues
  const pollFalDirectly = useCallback(async ({ job_id, fal_status_url, fal_response_url, fal_key }) => {
    videoPollingRef.current = true;
    const falHeaders = { Authorization: `Key ${fal_key}` };

    for (let i = 0; i < 200; i++) {
      if (!videoPollingRef.current) return;
      await new Promise(r => setTimeout(r, 5000));
      try {
        const sr = await fetch(fal_status_url, { headers: falHeaders });
        const sd = await sr.json();
        const status = (sd.status || "").toUpperCase();

        if (status === "COMPLETED") {
          // Fetch result from fal.ai
          const rr = await fetch(fal_response_url, { headers: falHeaders });
          const rd = await rr.json();
          const vUrl = rd?.video?.url || rd?.video_url || rd?.output?.video || "";

          setVideoUrl(vUrl);
          setVideoStatus("done");
          videoPollingRef.current = false;
          clearInterval(timerRef.current);

          // Step 3: Save to backend (Supabase + DB) — fire and forget
          fetch(`${API_URL}/video/save-ref`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
            body: JSON.stringify({
              job_id,
              video_url: vUrl,
              prompt: buildApiPrompt(),
              ratio,
              duration,
              resolution,
              character_names: slots.filter(s => s?.type === "character").map(s => s.name),
              scene_name: slots.find(s => s?.type === "scene")?.name || "",
            }),
          }).catch(() => {});
          return;
        }

        if (status === "FAILED") {
          setErrorMsg(sd.error || "Error en fal.ai");
          setVideoStatus("error");
          videoPollingRef.current = false;
          clearInterval(timerRef.current);
          return;
        }
      } catch (err) {
        console.error("fal.ai poll error:", err);
      }
    }
    setErrorMsg("Tiempo agotado esperando el vídeo (16 min)");
    setVideoStatus("error");
    videoPollingRef.current = false;
    clearInterval(timerRef.current);
  }, [prompt, ratio, duration, resolution, slots]);

  // --- Reset ---
  const handleReset = () => {
    setSlots(Array(9).fill(null));
    setSetAccepted(false);
    setBrief("");
    setPrompt("");
    setChatHistory([]);
    setVideoStatus("idle");
    setVideoUrl(null);
    setErrorMsg("");
    setElapsed(0);
    videoPollingRef.current = false;
    clearInterval(timerRef.current);
    localStorage.removeItem(STORAGE_KEY);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const canGenerate = slotCount > 0 && !generating;

  // --- Highlighted prompt (color aliases) ---
  const renderHighlightedPrompt = () => {
    if (!prompt) return null;
    const aliases = slots.filter(Boolean).map(s => ({ alias: s.alias, type: s.type }));
    if (aliases.length === 0) return <span>{prompt}</span>;

    // Build regex from aliases sorted by length desc
    const sorted = [...aliases].sort((a, b) => b.alias.length - a.alias.length);
    const pattern = sorted.map(a => `@${a.alias}`).join("|");
    const regex = new RegExp(`(${pattern})\\b`, "gi");

    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(prompt)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: prompt.substring(lastIndex, match.index), type: "text" });
      }
      const found = aliases.find(a => `@${a.alias}`.toLowerCase() === match[0].toLowerCase());
      parts.push({ text: match[0], type: found?.type || "text" });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < prompt.length) {
      parts.push({ text: prompt.substring(lastIndex), type: "text" });
    }

    return parts.map((p, i) =>
      p.type === "text"
        ? <span key={i}>{p.text}</span>
        : <span key={i} className={`vs-highlight vs-highlight--${p.type}`}>{p.text}</span>
    );
  };

  return (
    <div className="vs-page">
      {/* LEFT */}
      <div className="vs-left">
        <div className="vs-header">
          <h2 className="vs-title">Set de Grabación</h2>
          <div className="vs-header__actions">
            <button className="vs-btn-reset" onClick={handleReset}>Nuevo</button>
            {setAccepted && (
              <>
                <button className="vs-btn-edit-set" onClick={() => setSetAccepted(false)}>Editar set</button>
                <button
                  className={`cs-btn-generate ${generating ? "cs-btn-generate--loading" : ""}`}
                  disabled={!canGenerate}
                  onClick={handleGeneratePrompt}
                >
                  {generating ? "Generando..." : "Generar Prompt"}
                </button>
                <button
                  className={`vg-btn-generate-video ${videoStatus === "generating" ? "vg-btn-generate-video--loading" : ""}`}
                  disabled={!prompt || videoStatus === "generating" || slotCount === 0}
                  onClick={handleGenerateVideo}
                >
                  {videoStatus === "generating" ? `Generando ${formatTime(elapsed)}` : "Generar Video"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* FASE 1: Montar el set */}
        {!setAccepted && (
          <>
            <section className="vs-slots-section">
              <span className="vs-section-title">Monta el set ({slotCount}/9)</span>
              <div className="vs-slots-grid">
                {slots.map((slot, i) => (
                  <SlotCard
                    key={i}
                    slot={slot}
                    index={i}
                    onRemove={removeSlot}
                    onClickEmpty={() => setPickerIndex(i)}
                    onAliasChange={updateAlias}
                  />
                ))}
              </div>
            </section>
            <button
              className="vs-btn-accept"
              disabled={slotCount === 0}
              onClick={() => setSetAccepted(true)}
            >
              Aceptar set ({slotCount} {slotCount === 1 ? "referencia" : "referencias"})
            </button>
          </>
        )}

        {/* FASE 2: Promptear */}
        {setAccepted && (
          <>
            {/* Cinta horizontal con los slots aceptados */}
            <section className="vs-strip-section">
              <div className="vs-strip">
                <button className="vs-strip__btn" onClick={() => scrollReel(-1)}>{"\u25C0"}</button>
                <div className="vs-strip__frames" ref={reelRef}>
                  {slots.map((slot, i) => slot ? (
                    <div key={i} className={`vs-strip__item vs-strip__item--${slot.type}`}>
                      <img src={slot.url} alt={slot.alias} />
                      <span className="vs-strip__alias">@{slot.alias}</span>
                    </div>
                  ) : null)}
                </div>
                <button className="vs-strip__btn" onClick={() => scrollReel(1)}>{"\u25B6"}</button>
              </div>
            </section>

            {/* Brief */}
            <div className="vs-brief">
              <span className="vs-brief__label">Brief</span>
              <input
                className="vs-brief__input"
                value={brief}
                onChange={e => setBrief(e.target.value)}
                placeholder="Describe la escena brevemente... (ej: @Carlos camina por @Playa al atardecer hacia @Elena)"
              />
            </div>

            {/* Prompt editor */}
            <div className="vs-prompt-editor">
              <div className="vs-prompt-editor__header">
                <span className="vs-prompt-editor__title">Prompt cinematográfico</span>
                <span className="vs-prompt-editor__count">{prompt.length} / 10000</span>
              </div>
              <textarea
                ref={promptRef}
                className="vs-prompt-editor__textarea"
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="Pulsa 'Generar Prompt' o escribe manualmente. Usa @alias para referenciar las imágenes del set (ej: @Carlos, @Playa)."
                rows={8}
                maxLength={10000}
              />
              {prompt && (
                <div className="vs-prompt-editor__preview">
                  {renderHighlightedPrompt()}
                </div>
              )}
            </div>

            {/* Parameters bar */}
            <div className="vs-params-bar">
              <div className="vs-param">
                <span className="vs-param__label">Motor</span>
                <span className="vs-param__fixed">Seedance 2.0</span>
              </div>
              <div className="vs-param">
                <span className="vs-param__label">Ratio</span>
                <select className="vs-param__select" value={ratio} onChange={e => setRatio(e.target.value)}>
                  <option value="auto">Auto</option>
                  <option value="16:9">16:9</option>
                  <option value="9:16">9:16</option>
                  <option value="4:3">4:3</option>
                  <option value="1:1">1:1</option>
                  <option value="3:4">3:4</option>
                  <option value="21:9">21:9</option>
                </select>
              </div>
              <div className="vs-param">
                <span className="vs-param__label">Duración</span>
                <select className="vs-param__select" value={duration} onChange={e => setDuration(e.target.value)}>
                  <option value="auto">Auto</option>
                  <option value="5">5s</option>
                  <option value="8">8s</option>
                  <option value="10">10s</option>
                  <option value="15">15s</option>
                </select>
              </div>
              <div className="vs-param">
                <span className="vs-param__label">Resolución</span>
                <select className="vs-param__select" value={resolution} onChange={e => setResolution(e.target.value)}>
                  <option value="720p">720p</option>
                  <option value="480p">480p</option>
                </select>
              </div>
              <div className="vs-param">
                <span className="vs-param__label">Audio</span>
                <button className={`vg-toggle ${soundFx ? "vg-toggle--on" : ""}`} onClick={() => setSoundFx(!soundFx)}>
                  {soundFx ? "ON" : "OFF"}
                </button>
              </div>
            </div>
          </>
        )}

        {errorMsg && <div className="cs-error">{errorMsg}</div>}
      </div>

      {/* RIGHT — Video preview + Refine */}
      <div className="vs-right">
        <div className="vs-video-preview">
          <h2 className="cs-panel__title">Video</h2>
          <div className="vs-video-preview__container">
            {videoStatus === "idle" && !videoUrl && (
              <div className="vs-video-preview__empty">
                <span className="vs-video-preview__icon">&#9654;</span>
                <p>{setAccepted ? "Genera un prompt y pulsa Generar Video" : "Monta el set y pulsa Aceptar"}</p>
              </div>
            )}
            {videoStatus === "generating" && (
              <div className="vs-video-preview__empty">
                <span className="cs-spinner" />
                <p>Generando vídeo... {formatTime(elapsed)}</p>
              </div>
            )}
            {videoStatus === "done" && videoUrl && (
              <div className="vs-video-preview__player">
                <video controls autoPlay loop className="vs-video-preview__video">
                  <source src={videoUrl} type="video/mp4" />
                </video>
                <div className="vs-video-preview__actions">
                  <a href={videoUrl} download="video_set.mp4" className="vg-btn-download">Descargar</a>
                </div>
              </div>
            )}
            {videoStatus === "error" && (
              <div className="vs-video-preview__empty">
                <p className="cs-error" style={{ margin: 0 }}>{errorMsg || "Error al generar"}</p>
              </div>
            )}
          </div>
        </div>

        {setAccepted && (
          <>
            <h2 className="cs-panel__title" style={{ marginTop: 8 }}>Refinar prompt</h2>
            <div className="vg-chat">
              <div className="vg-chat__messages">
                {chatHistory.length === 0 && (
                  <div className="vg-chat__empty">
                    <span className="vg-chat__empty-icon">&#9998;</span>
                    <p>Pide cambios en el prompt</p>
                  </div>
                )}
                {chatHistory.map((msg, i) => (
                  <div key={i} className={`vg-chat__msg vg-chat__msg--${msg.role}`}>{msg.text}</div>
                ))}
                {refining && <div className="vg-chat__msg vg-chat__msg--assistant vg-chat__msg--loading">Pensando...</div>}
              </div>
              <div className="vg-chat__input-bar">
                <button className="vg-chat__quick-btn" onClick={() => setRefineInput("Mejorar prompt")}>Mejorar prompt</button>
                <div className="vg-chat__input-wrap">
                  <input
                    className="vg-chat__input"
                    value={refineInput}
                    onChange={e => setRefineInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleRefine(); } }}
                    placeholder="Añade detalles..."
                    disabled={refining || !prompt}
                  />
                  <button className="vg-chat__send" onClick={handleRefine} disabled={refining || !refineInput.trim() || !prompt}>&#x2191;</button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Asset picker modal */}
      {pickerIndex !== null && (
        <AssetPicker
          scenes={scenes}
          characters={characters}
          artifacts={artifacts}
          onSelect={asset => assignSlot(pickerIndex, asset)}
          onClose={() => setPickerIndex(null)}
        />
      )}
    </div>
  );
}
